import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { X, FileText, FileDiff } from 'lucide-react'
import { ExportButton } from './ExportButton'
import { VisualizationButton } from './VisualizationButton'
import { ProposalDiffReviewModal } from './ProposalDiffReviewModal'

interface ProposalModalProps {
  isOpen: boolean
  onClose: () => void
  proposal: string
  projectId: string
  projectName?: string
  onProposalUpdated?: (markdown: string) => void
}

export function ProposalModal({
  isOpen,
  onClose,
  proposal,
  projectId,
  projectName,
  onProposalUpdated,
}: ProposalModalProps) {
  const [showDiffReview, setShowDiffReview] = useState(false)

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.6)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-lg shadow-2xl"
        style={{ background: 'var(--warm-white)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--sand)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-md"
              style={{ background: 'rgba(200, 88, 53, 0.1)' }}
            >
              <FileText className="w-5 h-5" style={{ color: 'var(--terracotta)' }} />
            </div>
            <div>
              <h2
                className="text-xl font-semibold"
                style={{
                  fontFamily: 'Crimson Text, Georgia, serif',
                  color: 'var(--charcoal)',
                }}
              >
                Project Proposal
              </h2>
              <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                Generated proposal document
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ExportButton proposal={proposal} projectId={projectId} />
            <VisualizationButton projectId={projectId} projectName={projectName} />
            <button
              onClick={() => setShowDiffReview(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-md font-medium transition-colors"
              style={{
                background: 'rgba(200, 88, 53, 0.08)',
                color: 'var(--terracotta)',
                fontFamily: 'DM Sans, sans-serif',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(200, 88, 53, 0.14)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(200, 88, 53, 0.08)'
              }}
            >
              <FileDiff className="w-4 h-4" />
              Review Changes
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md transition-colors"
              style={{
                background: 'transparent',
                color: 'var(--charcoal-light)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(200, 88, 53, 0.1)';
                e.currentTarget.style.color = 'var(--terracotta)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--charcoal-light)';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto px-8 py-6"
          style={{
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1
                    className="text-3xl font-bold mb-6 pb-3 border-b"
                    style={{
                      fontFamily: 'Crimson Text, Georgia, serif',
                      color: 'var(--charcoal)',
                      borderColor: 'var(--sand)',
                    }}
                  >
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2
                    className="text-2xl font-semibold mb-4 mt-8"
                    style={{
                      fontFamily: 'Crimson Text, Georgia, serif',
                      color: 'var(--charcoal)',
                    }}
                  >
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3
                    className="text-xl font-medium mb-3 mt-6"
                    style={{
                      fontFamily: 'Crimson Text, Georgia, serif',
                      color: 'var(--charcoal)',
                    }}
                  >
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p
                    className="mb-4 leading-relaxed"
                    style={{ color: 'var(--charcoal)' }}
                  >
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="mb-4 ml-6 space-y-2" style={{ color: 'var(--charcoal)' }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-4 ml-6 space-y-2" style={{ color: 'var(--charcoal)' }}>
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li style={{ color: 'var(--charcoal)' }}>{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold" style={{ color: 'var(--charcoal)' }}>
                    {children}
                  </strong>
                ),
                hr: () => (
                  <hr className="my-8" style={{ borderColor: 'var(--sand)' }} />
                ),
                blockquote: ({ children }) => (
                  <blockquote
                    className="border-l-4 pl-4 py-2 my-4 italic"
                    style={{
                      borderColor: 'var(--terracotta)',
                      background: 'rgba(200, 88, 53, 0.05)',
                      color: 'var(--charcoal-light)',
                    }}
                  >
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code
                    className="px-1.5 py-0.5 rounded text-sm font-mono"
                    style={{
                      background: 'var(--cream)',
                      color: 'var(--charcoal)',
                    }}
                  >
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre
                    className="p-4 rounded-md overflow-x-auto mb-4"
                    style={{
                      background: 'var(--cream)',
                      border: `1px solid var(--sand)`,
                    }}
                  >
                    {children}
                  </pre>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-6">
                    <table className="w-full border-collapse" style={{ borderColor: 'var(--sand)' }}>
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead style={{ background: 'var(--cream)' }}>{children}</thead>
                ),
                th: ({ children }) => (
                  <th
                    className="px-4 py-2 text-left font-semibold border"
                    style={{
                      color: 'var(--charcoal)',
                      borderColor: 'var(--sand)',
                    }}
                  >
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td
                    className="px-4 py-2 border"
                    style={{
                      color: 'var(--charcoal)',
                      borderColor: 'var(--sand)',
                    }}
                  >
                    {children}
                  </td>
                ),
              }}
            >
              {proposal}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex justify-end"
          style={{ borderColor: 'var(--sand)', background: 'var(--cream)' }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md font-medium transition-colors"
            style={{
              background: 'var(--terracotta)',
              color: 'white',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--terracotta-dark)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--terracotta)';
            }}
          >
            Close
          </button>
        </div>
      </div>

      <ProposalDiffReviewModal
        isOpen={showDiffReview}
        onClose={() => setShowDiffReview(false)}
        projectId={projectId}
        onCommitted={(mergedMarkdown) => {
          onProposalUpdated?.(mergedMarkdown)
        }}
      />
    </div>
  )
}
